import { idOf, punktartRow } from '../../data-model';

/**
 * **Die Favoriten der Punktart-Auswahl: die am haeufigsten gewaehlten Punktarten.**
 *
 * Gefuehrt wird das vom `RecentValuesService` der Bibliothek — derselbe Dienst,
 * der bei Substrat und Horizonten die Favoriten liefert. Hier stehen nur die
 * Dinge, die an der Punktart haengen: der Schluessel und die Umrechnung von
 * Alt-Daten.
 *
 * Der Schluessel ist bewusst DERSELBE wie frueher. Darin stand bisher die Liste
 * „Zuletzt verwendet" (bis zu fuenf Nummern, zuletzt gewaehlte zuerst), und die
 * ist eine gueltige Historie fuer den Favoriten-Dienst: Jeder Wert kommt genau
 * einmal vor, also zaehlen alle gleich haeufig, und bei gleicher Haeufigkeit
 * gewinnt der zuletzt benutzte. Die bisherige Reihenfolge bleibt beim Umstieg
 * damit genau erhalten — niemand verliert seine Liste.
 */
export const PUNKTART_FAVORITEN_KEY = 'recentPointTypes';

/** Noch aeltere Fassung: EINE Punktart statt einer Liste. */
export const PUNKTART_EINZELWERT_KEY = 'recentPointType';

/** So viele Favoriten stehen oben im Auswahl-Fenster (wie bei Substrat/Horizont). */
export const PUNKTART_FAVORITEN_MAX = 8;

/**
 * Der Ausschnitt des Speicherdienstes, den die Favoriten brauchen. Als eigener
 * Vertrag, damit die Regel ohne Angular pruefbar ist.
 */
export interface PunktartSpeicher {
	getSync(key: string): any;
	setSync(key: string, value: any): void;
}

/**
 * Ein einzelner Wert als gepruefte Punktart-Nummer; `''` wenn unbekannt.
 *
 * Geprueft wird gegen `punktartRow`, also gegen dieselbe Liste, aus der das
 * Auswahl-Fenster seine Kacheln nimmt (zentraler Listen-Speicher, notfalls die
 * mitgelieferten Vorgaben). So folgt die Pruefung den Aenderungen im
 * Listen-Editor: Eine geloeschte Punktart faellt aus den Favoriten heraus,
 * statt als Kachel stehenzubleiben, die niemand mehr waehlen darf.
 */
export function punktartNummer(wert: unknown): string {
	if (wert == null) return '';
	const n = idOf(String(wert));
	if (!Number.isFinite(n) || n <= 0) return '';
	return punktartRow(String(n)) ? String(n) : '';
}

/**
 * Bringt eine gespeicherte Historie auf reine Nummern und wirft weg, was die
 * Punktarten-Liste nicht (mehr) fuehrt.
 *
 * Alt-Daten stehen als Voll-String da („6_Tiefbohrung"), manche als Zahl. Ohne
 * die Umrechnung zaehlte dieselbe Punktart mehrfach und waere in den Favoriten
 * zu selten. Doppelte bleiben dagegen absichtlich stehen — sie SIND die
 * Haeufigkeit.
 */
export function punktartHistorieAufNummern(roh: unknown): string[] {
	if (!Array.isArray(roh)) return [];
	const nummern: string[] = [];
	for (const wert of roh) {
		const n = punktartNummer(wert);
		if (n) nummern.push(n);
	}
	return nummern;
}

/**
 * Raeumt die gespeicherte Historie auf, bevor die Favoriten gelesen werden:
 * Alt-Strings werden zu Nummern, Entfallenes fliegt raus, und die ganz alte
 * Einzelwahl wird uebernommen, solange es noch gar keine Liste gibt.
 *
 * Laeuft bei jedem Oeffnen des Fensters — das ist ein Lesevorgang, und
 * geschrieben wird nur, wenn sich wirklich etwas aendert.
 *
 * **Die Einzelwahl zaehlt NUR, solange keine Liste da ist.** Danach steht sie in
 * der Liste; ein zweites Mal uebernommen, wuerde sie bei jedem Oeffnen haeufiger
 * und stuende fuer immer ganz vorn.
 */
/**
 * Der Ausschnitt des Favoriten-Dienstes, den das Merken braucht
 * (`RecentValuesService.track`).
 */
export interface PunktartMerker {
	track(key: string, value: string | undefined, max?: number): string[];
}

/**
 * Merkt sich eine Punktart. **Gerufen wird das beim SPEICHERN, nicht beim
 * Antippen einer Kachel** (Nutzer-Entscheidung 19.09.2026): Wer im Fenster
 * etwas antippt und dann abbricht, hat sich fuer nichts entschieden, und die
 * Vorbelegung des naechsten Punktes darf davon nicht abhaengen.
 *
 * Aufgeraeumt wird vorher, damit ein Alt-String in der Historie nicht als
 * eigene Punktart weiterzaehlt.
 */
export function merkePunktart(merker: PunktartMerker, storage: PunktartSpeicher, type: string): void {
	const nummer = punktartNummer(type);
	if (!nummer) return;
	raeumePunktartHistorieAuf(storage);
	merker.track(PUNKTART_FAVORITEN_KEY, nummer, PUNKTART_FAVORITEN_MAX);
}

/**
 * Merkt die Punktart eines gespeicherten Punktes — aber nur, wenn sie in dieser
 * Bearbeitung auch **gewaehlt** wurde.
 *
 * Ohne diesen Vergleich zaehlte jedes Speichern mit: Wer zwanzig alte Moorpunkte
 * oeffnet und an jedem die Nummer berichtigt, haette hinterher „Moorpunkt" als
 * Vorbelegung, ohne die Punktart je angefasst zu haben.
 */
export function merkeGewaehltePunktart(
	merker: PunktartMerker,
	storage: PunktartSpeicher,
	type: string,
	typeBeimLaden: string,
): void {
	if (punktartNummer(type) === punktartNummer(typeBeimLaden)) return;
	merkePunktart(merker, storage, type);
}

/**
 * Die zuletzt gewaehlte Punktart; `''` wenn nichts (mehr) gemerkt ist.
 *
 * Die Historie steht neueste zuerst — der erste Eintrag ist also die letzte
 * Wahl. Das ist bewusst NICHT dieselbe Frage wie die der Favoriten: Oben im
 * Fenster stehen die haeufigsten, vorbelegt wird die zuletzt gewaehlte.
 */
export function zuletztGewaehltePunktart(storage: PunktartSpeicher): string {
	raeumePunktartHistorieAuf(storage);
	const historie = storage.getSync(PUNKTART_FAVORITEN_KEY);
	return Array.isArray(historie) ? (historie[0] ?? '') : '';
}

export function raeumePunktartHistorieAuf(storage: PunktartSpeicher): void {
	const roh = storage.getSync(PUNKTART_FAVORITEN_KEY);
	const listeDa = Array.isArray(roh);
	const quelle = listeDa ? roh : [storage.getSync(PUNKTART_EINZELWERT_KEY)];
	const neu = punktartHistorieAufNummern(quelle);
	if (listeDa && gleich(roh as unknown[], neu)) return;
	if (!listeDa && neu.length === 0) return;
	storage.setSync(PUNKTART_FAVORITEN_KEY, neu);
}

function gleich(a: unknown[], b: string[]): boolean {
	return a.length === b.length && a.every((v, i) => v === b[i]);
}
